import React, { useContext, useEffect } from 'react';

import * as Styled from './styled';
import { TradingContext } from '@trader/contexts';
import {
  formatNumber,
  getMaxDigitWithDot,
  getRequiredOpenCostQuantity,
} from '@trader/utils';
import { OrderInput } from '../../../components/orderInput';
import { Select } from '@trader/components';
import { IChangeFieldEvent, TOptions } from '@trader/types';
import { minimumPriceFractionDigits } from '../PurchaseDetails';
import { observer } from 'mobx-react-lite';
import { useMst } from '@trader/store';
import { useI18next } from '@trader/services';

export const Protection: React.FC = observer(() => {
  const store = useMst();
  const { translate } = useI18next();
  const {
    tradingProtection: {
      maxProfitValue,
      maxLossValue,
      minLossValue,
      minProfitValue,
      isSwitchLoss,
      isSwitchProfit,
      marketAmount,
    },
    currencyConvert,
    instrument: { general, market },
    switchTakeProfit,
    switchStopLoss,
    errors,
    getRequiredOpenCost,
    handleChangeProtection,
    handleChangeUnit,
    handleChangePips,
    realTimePrice,
  } = useContext(TradingContext);

  const quantity = getRequiredOpenCostQuantity(
    market.amountVolume,
    market.minAmountValue
  );

  useEffect(() => {
    getRequiredOpenCost(general.symbol, market.billType, quantity, 0);
  }, [general.symbol, market.billType, quantity]);

  const unitProfit = market.protection.unitProfit;
  const unitStopLoss = market.protection.unitStopLoss;
  const isUnitProfitPips = unitProfit !== 'Rate';
  const isUnitStopLossPips = unitStopLoss !== 'Rate';

  const toggleTakeProfit = (value: boolean): void => {
    if (marketAmount) {
      switchTakeProfit(value);
    }
  };

  const toggleStopLoss = (value: boolean): void => {
    if (marketAmount) {
      switchStopLoss(value);
    }
  };

  const unitOptions: TOptions = [
    {
      title: translate('COMMON.LABELS.RATE'),
      value: 'Rate',
      id: 'Rate',
    },
    {
      title: translate('COMMON.LABELS.PIPS'),
      value: 'Pips',
      id: 'Pips',
    },
  ];

  const handleUnitChange = (event: IChangeFieldEvent<TOptions>) => {
    const value = event.target.value;
    const name = event.target.name;
    handleChangeUnit(value[0].value, name);
  };

  const getLossProfit = (type: 'stopLoss' | 'takeProfit') => {
    const protectionValue =
      type === 'stopLoss'
        ? +market.protection.lossValue
        : +market.protection.profitValue;

    const price =
      market.billType === 'Sell' ? realTimePrice.ask : realTimePrice.bid;

    const result =
      (Number(market.amountVolume) *
        Math.abs(protectionValue - Number(price))) /
      currencyConvert;

    return formatNumber(result, minimumPriceFractionDigits);
  };

  return (
    <Styled.Root>
      <Styled.Content>
        <Styled.Protection>
          <Styled.TakeProfit>
            <Styled.Toggle
              shouldHideControlInfo
              label={translate('COMMON.LABELS.TAKE_PROFIT')}
              checked={isSwitchProfit}
              onChange={event => toggleTakeProfit(event.target.checked)}
            />
            {isSwitchProfit && (
              <Styled.InputWrapper>
                <Styled.Select>
                  <Select
                    options={unitOptions}
                    value={unitOptions.filter(o => o.value === unitProfit)}
                    onChange={handleUnitChange}
                    name='unitProfit'
                  />
                </Styled.Select>
                <Styled.Label>
                  <Styled.InputLabel>
                    {translate('COMMON.LABELS.PROFIT')}:{' '}
                    {store.user.portfolio.currencySymbol}
                    {getLossProfit('takeProfit')}
                  </Styled.InputLabel>
                  {isUnitProfitPips && (
                    <Styled.InputLabel>
                      {translate('COMMON.LABELS.RATE')}:{' '}
                      {market.protection.profitValue}
                    </Styled.InputLabel>
                  )}
                </Styled.Label>
                <OrderInput
                  errorMsg={isSwitchProfit ? errors.takeProfitError : ''}
                  fixDigitAfterDot={
                    isUnitProfitPips ? 0 : getMaxDigitWithDot(general.pipSize)
                  }
                  disabled={!isSwitchProfit}
                  shouldCheckActivityOfButtons={!isUnitProfitPips}
                  step={isUnitProfitPips ? 1 : general.pipSize}
                  maxValue={maxProfitValue}
                  minValue={minProfitValue}
                  value={
                    isUnitProfitPips
                      ? market.protection.profitPips
                      : market.protection.profitValue
                  }
                  onChangeInput={value =>
                    isUnitProfitPips
                      ? handleChangePips(value, 'profitPips')
                      : handleChangeProtection(value, 'profitValue')
                  }
                />
              </Styled.InputWrapper>
            )}
          </Styled.TakeProfit>
          <Styled.StopLoss>
            <Styled.Toggle
              shouldHideControlInfo
              label={translate('COMMON.LABELS.STOP_LOSS')}
              checked={isSwitchLoss}
              onChange={event => toggleStopLoss(event.target.checked)}
            />
            {isSwitchLoss && (
              <Styled.InputWrapper>
                <Styled.Select>
                  <Select
                    options={unitOptions}
                    value={unitOptions.filter(o => o.value === unitStopLoss)}
                    onChange={handleUnitChange}
                    name='unitStopLoss'
                  />
                </Styled.Select>
                <Styled.Label>
                  <Styled.InputLabel>
                    {translate('COMMON.LABELS.LOSS')}:{' '}
                    {store.user.portfolio.currencySymbol}
                    {getLossProfit('stopLoss')}
                  </Styled.InputLabel>
                  {isUnitStopLossPips && (
                    <Styled.InputLabel>
                      {translate('COMMON.LABELS.RATE')}:{' '}
                      {market.protection.lossValue}
                    </Styled.InputLabel>
                  )}
                </Styled.Label>
                <OrderInput
                  errorMsg={isSwitchLoss ? errors.stopLossError : ''}
                  fixDigitAfterDot={
                    isUnitStopLossPips ? 0 : getMaxDigitWithDot(general.pipSize)
                  }
                  step={isUnitStopLossPips ? 1 : general.pipSize}
                  disabled={!isSwitchLoss}
                  shouldCheckActivityOfButtons={!isUnitStopLossPips}
                  maxValue={maxLossValue}
                  minValue={minLossValue}
                  value={
                    isUnitStopLossPips
                      ? market.protection.stopLossPips
                      : market.protection.lossValue
                  }
                  onChangeInput={value =>
                    isUnitStopLossPips
                      ? handleChangePips(value, 'stopLossPips')
                      : handleChangeProtection(value, 'lossValue')
                  }
                />
              </Styled.InputWrapper>
            )}
          </Styled.StopLoss>
        </Styled.Protection>
      </Styled.Content>
    </Styled.Root>
  );
});
